import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * PD-1516 "AI Dictate" — light-formatting pass over a clinician's spoken
 * dictation (captured client-side via the browser's speech recognition).
 * Nothing here touches the note: the FE shows the result as a preview and the
 * provider accepts or discards it. Whole-note dictation does NOT come through
 * here — it is a transcript source for `scribeGenerateDraft`.
 */

/**
 * One medication the model heard in the dictation. `name` is the bare drug
 * name (no dose/route) — it is what gets arbitrated against RxNorm; an
 * unmatched or model-doubted medication must be confirmed by the provider
 * before the preview can be accepted.
 */
export const dictateMedicationSchema = z.object({
  /** Verbatim from the dictation, e.g. "eBook profent 200". */
  original: z.string(),
  /** As written into the formatted text, e.g. "ibuprofen 200". */
  normalized: z.string(),
  /** Bare drug name for terminology lookup, e.g. "ibuprofen". */
  name: z.string(),
  confident: z.boolean(),
  /** Why the provider must review, e.g. "unit not stated". */
  reason: z.string().optional(),
})
export type DictateMedication = z.infer<typeof dictateMedicationSchema>

/** Flow (a) field dictation and flow (c) selected-text replacement. */
export const scribeDictateFormat = oc
  .route({ method: 'POST', path: '/v2/scribe/dictate/format' })
  .input(
    z.object({
      encounterId: z.string().min(1),
      dictation: z.string().min(1).max(10_000),
      mode: z.enum(['field', 'replacement']),
      /** Human label of the destination field, e.g. "History of Present Illness". */
      fieldLabel: z.string().max(200).optional(),
      /** Replacement mode only: the text the dictation replaces. */
      selectedText: z.string().max(10_000).optional(),
    }),
  )
  .output(
    z.object({
      text: z.string(),
      medications: z.array(dictateMedicationSchema),
    }),
  )
